import { useUser } from "@/api/user.store";
import { getUserDB } from "@/api/DB";
import { v4 as uuid } from "uuid";
import type { LocalModelInfo } from "@/api/DB";

export function useWorkspaceModelsDB() {
  const { user } = useUser();

  /**
   * Returns the local db workspace table
   */
  function getModelsDB() {
    const userDB = getUserDB(user.value?.id);

    type TableWithExtraMethods = typeof userDB.models & {
      create?: (
        newData: Partial<LocalModelInfo>
      ) => Promise<LocalModelInfo | undefined>;
      updateAndGet?: (
        pk: string | number,
        newData: Partial<LocalModelInfo>
      ) => Promise<LocalModelInfo | undefined>;
    };

    const table: TableWithExtraMethods = userDB.models;

    table.create = async function (newData: Partial<LocalModelInfo>) {
      const now = new Date().toISOString();

      // Local models get a temporary unique id that will be replaced
      // as soon as they are synchronized with the cloud.
      newData.id = uuid();

      // Set default properties
      newData.isNew = true;
      newData.modified = now;
      newData.created = now;
      newData.deleted = null;
      newData.type = newData?.type ?? "workspace_model";

      await this.add(newData as LocalModelInfo);

      return await this.get(newData.id);
    };

    function formatForInsert<T>(data: T & { modified?: string }) {
      // make sure to always update the modified timestamp
      if (!("modified" in data)) {
        data.modified = new Date().toISOString();
        console.warn(
          "%cadding 'modified' to data",
          "font-size: 10px; background-color: #CCC;",
          data.modified
        );
      }

      return data;
    }

    const update = table.update.bind(table);
    table.update = function (key, changes) {
      console.debug(`✍ update model ${key}`, changes);
      return update(key, formatForInsert(changes));
    };

    table.updateAndGet = async function (
      pk: string | number,
      newData: Partial<LocalModelInfo>
    ) {
      const dbModel = await this.get(pk);

      if (dbModel) {
        if (newData == null) {
          await this.delete(pk);
          return undefined;
        } else {
          await table.update(pk, newData);
        }
      } else {
        console.debug(`✍ set model ${pk}`, newData);
        await this.put(formatForInsert(newData) as LocalModelInfo);
      }

      // retrieve based on the new data
      // (this makes sure that the new model is returned
      // even if the id was changed)
      const newId = newData?.id != null ? newData?.id : pk;
      return await this.get(newId);
    };

    return table as Required<TableWithExtraMethods>;
  }

  return {
    getModelsDB
  };
}
